import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from "axios";
import { registerMediaOrigin } from "./mediaUrl";
import { preloadLocale } from "../i18n";
import { readOperatorSupport, returnFromOperatorSupport } from "./operatorSupportSession";

/**
 * Central Axios instance.
 * – Base URL from env
 * – Auth token injected automatically via `withCredentials` cookie
 * – 401 → notify AuthContext via registered callback (no hard redirects)
 */
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_SERVER_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const support = readOperatorSupport();
  const path = config.url || "";
  // Never put the scoped token on arbitrary remote URLs or the operator's
  // explicit exit/logout request. Ordinary support reads retain the user JWT.
  if (
    support &&
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !path.startsWith("/api/admin/support-sessions") &&
    path !== "/auth/logout"
  )
    config.headers.set("X-Customy-Support-Session", support.token);
  return config;
});

type UnauthorizedHandler = () => void;

// Callback registered by AuthContext to handle 401s within React
let onUnauthorized: UnauthorizedHandler | null = null;

export function setOnUnauthorized(cb: UnauthorizedHandler | null) {
  onUnauthorized = cb;
}

// Global 401 handler — delegate to AuthContext instead of hard redirect
api.interceptors.response.use(
  (response: AxiosResponse) => {
    registerMediaOrigin(response.headers["x-media-origin"]);
    return response;
  },
  async (error: AxiosError<{ code?: string; error?: string; retryAfterSeconds?: number }>) => {
    const code = error.response?.data?.code;
    if (code === "SUPPORT_SESSION_EXPIRED" || code === "SUPPORT_SESSION_INVALID")
      returnFromOperatorSupport();
    if (code === "SUPPORT_READ_ONLY") {
      const locale = typeof document === "undefined" ? "en" : document.documentElement.lang;
      const translations = await preloadLocale(locale).catch(() => null);
      const message = translations?.operatorAdmin?.support?.readOnlyError;
      if (typeof message === "string" && error.response) {
        error.response.data.error = message;
        error.message = message;
      }
    }
    if (error.response?.status === 401) {
      if (onUnauthorized) onUnauthorized();
    }
    if (error.response?.status === 429 && error.response.data?.code === "api_rate_limited") {
      const locale = typeof document === "undefined" ? "en" : document.documentElement.lang;
      const translations = await preloadLocale(locale).catch(() => null);
      const template = translations?.common?.apiRateLimited;
      if (typeof template === "string") {
        const seconds = Math.max(1, Number(error.response.data.retryAfterSeconds) || 60);
        const message = template.replace("{{seconds}}", String(seconds));
        error.response.data.error = message;
        error.message = message;
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Session 19 — Cursor-Pagination Helpers.
 *
 * Der Server liefert paginierte Listen (Mockups, Templates, Folders) als
 * `{ items, nextCursor, hasMore }`. Diese Helpers abstrahieren das fuer
 * zwei Use-Cases:
 *
 * - `getPage`:       eine einzelne Seite (fuer useInfiniteQuery + virtualized
 *                    scroll — der Standardweg).
 * - `getAllPages`:   auto-paginiert alles (fuer Migrations-Call-Sites die
 *                    heute unpaginiert konsumieren und nicht sofort virtualisiert
 *                    werden koennen). Sichert Shape-Kompatibilitaet.
 */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageParams {
  cursor?: string | null;
  limit?: number;
  q?: string;
  [key: string]: unknown;
}

function buildParams(params: PageParams): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    out[key] = value;
  }
  return out;
}

export async function getPage<T>(path: string, params: PageParams = {}): Promise<Page<T>> {
  const res = await api.get<Page<T>>(path, { params: buildParams(params) });
  return res.data;
}

/**
 * Sammelt alle Seiten ein — **nur** fuer Call-Sites wo virtualized/paginated
 * UI (noch) nicht sinnvoll ist. Safety-Cap bei 50 Seiten (max. 25k Records
 * bei default limit=500) um Endlos-Loops zu vermeiden falls der Server
 * wegen Bug kein nextCursor=null liefert.
 */
export async function getAllPages<T>(
  path: string,
  params: Omit<PageParams, "cursor"> = {},
): Promise<T[]> {
  const MAX_PAGES = 50;
  const items: T[] = [];
  let cursor: string | null = null;
  for (let i = 0; i < MAX_PAGES; i += 1) {
    const page: Page<T> = await getPage<T>(path, { ...params, cursor });
    items.push(...page.items);
    if (!page.hasMore || !page.nextCursor) return items;
    cursor = page.nextCursor;
  }
  // Safety-Break ohne Exception: Aufrufer bekommen was sie haben, ein
  // Warning landet im Devtools-Log.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[api.getAllPages] ${path} reached MAX_PAGES=${MAX_PAGES} — truncating`);
  }
  return items;
}

/**
 * Session 20 — Sicherer Download-Helper fuer Blob-URLs.
 *
 * Ersetzt das duplizierte `fetch(url).then(r => r.blob())` + `a.click()` +
 * setTimeout-revoke-URL-Pattern das sich in DownloadsPage, ResultGallery,
 * SaveMockup, BulkGenerationTab, useCanvasAssetManager verteilt hat.
 *
 * - Fuer /api/*-Pfade: nutzt den Axios-Client (Cookies + Retry + Timeout +
 *   onUnauthorized-Handler).
 * - Fuer externe/absolute URLs (z.B. presigned S3): rohes fetch OHNE
 *   credentials — S3 lehnt Cookies ab und wir wollen unsere Auth nicht
 *   an externe Hosts leaken.
 * - Beide Pfade unterstuetzen AbortSignal, damit User-Navigation-Canceln
 *   den Download sauber abbricht statt ihn im Hintergrund fertig zu laden.
 *
 * Returns a Promise<Blob>. Download via browser save-dialog ist in
 * `saveBlobAs(blob, filename)` separat — so kann der Caller Blobs auch
 * zur weiteren Verarbeitung verwenden.
 */
export interface FetchBlobOptions {
  signal?: AbortSignal;
  /** Millisekunden bis Timeout-Abort. 0 oder undefined = kein Timeout. */
  timeoutMs?: number;
}

function isInternalApiPath(url: string): boolean {
  // /api/* oder relativer Pfad ohne Protokoll-Prefix
  if (url.startsWith("/api/")) return true;
  if (url.startsWith("/")) return true; // relativer Pfad
  return false;
}

export async function fetchBlob(url: string, opts: FetchBlobOptions = {}): Promise<Blob> {
  const { signal, timeoutMs } = opts;

  // Timeout via AbortController, wenn gewuenscht. Der Caller-signal wird
  // mit unserem Timeout-signal kombiniert (abort early, whichever first).
  const controller = new AbortController();
  const combinedSignal = controller.signal;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  if (timeoutMs && timeoutMs > 0) {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  }
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  try {
    if (isInternalApiPath(url)) {
      // Eigener Server → Axios-Instance (Cookies, Retry, Interceptors).
      const res = await api.get(url, { responseType: "blob", signal: combinedSignal });
      return res.data as Blob;
    }
    // Externe URL (presigned S3 etc.) → rohes fetch ohne credentials.
    const response = await fetch(url, {
      signal: combinedSignal,
      credentials: "omit",
    });
    if (!response.ok) {
      throw new Error(`Download failed: HTTP ${response.status} ${response.statusText}`);
    }
    return await response.blob();
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/**
 * Triggert Browser-Save-Dialog fuer einen Blob. Kuemmert sich um
 * DOM-Append (Firefox/Safari-Anforderung) und Revoke-nach-click mit
 * setTimeout gegen Race-Condition.
 */
export function saveBlobAs(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Convenience: Fetch + Save-as in einem Aufruf. Sinnvoll fuer Download-
 * Buttons wo man direkt den Browser-Save-Dialog triggert.
 */
export async function downloadBlob(
  url: string,
  filename: string,
  opts: FetchBlobOptions = {},
): Promise<void> {
  const blob = await fetchBlob(url, opts);
  saveBlobAs(blob, filename);
}

export default api;
